import { useMemo } from "react";
import useActiveProfile from "./useActiveProfile";

function normalizePlanName(subscription) {
    const raw = subscription?.plan?.name;
    return raw ? String(raw).trim().toLowerCase() : "";
}

function isTrial(subscription) {
    const name = normalizePlanName(subscription);

    // Adjust these heuristics to match your actual backend fields
    if (name.includes("trial")) return true;          // e.g. "Trial", "Elite Trial", "Trial - Elite"
    if (subscription?.isTrial) return true;           // camelCase flag
    if (subscription?.is_trial) return true;          // snake_case flag
    if (subscription?.plan?.key === "trial") return true; // if you use a key

    return false;
}

function getPlanType(subscription) {
    const name = normalizePlanName(subscription);

    if (!name || name === "free") return "free";

    if (isTrial(subscription)) {
        return "elite";
    }

    if (name.startsWith("enterprise")) return "enterprise";
    if (name.startsWith("pro")) return "pro";
    if (name.startsWith("elite")) return "elite";

    return "paid-other";
}

/**
 * useActiveProfileOrgProOrElite
 *
 * Checks ONLY the activeProfile:
 *   activeProfile.profileOrganizations[].organization.activeSubscription.plan.name
 *
 * Returns:
 *   - hasAnyOrgProOrElite: boolean (any org with Pro / Elite / Trial)
 *   - proOrEliteOrganizations: Organization[] (Pro + Elite + Trial)
 *   - hasAnyOrgPro: boolean
 *   - hasAnyOrgElite: boolean (includes Trial)
 *   - proOrganizations: Organization[]
 *   - eliteOrganizations: Organization[] (includes Trial)
 *   - proOnly: boolean (only Pro, no Elite/Trial)
 *   - eliteOnly: boolean (only Elite/Trial, no Pro)
 */
export default function useActiveProfileOrgProOrElite() {
    const { activeProfile } = useActiveProfile();

    const profileOrganizations = useMemo(
        () => activeProfile?.profileOrganizations ?? [],
        [activeProfile]
    );

    const {
        proOrganizations,
        eliteOrganizations,
        enterpriseOrganizations,
        proOrEliteOrganizations,
    } = useMemo(() => {
        const pros = [];
        const elites = [];
        const enterprises = [];
        const proOrElite = [];

        for (const po of profileOrganizations) {
            const org = po?.organization;
            if (!org) continue;

            const planType = getPlanType(org?.activeSubscription);

            if (planType === "pro") {
                pros.push(org);
                proOrElite.push(org);
            } else if (planType === "elite") {
                elites.push(org);
                proOrElite.push(org);
            } else if (planType === "enterprise") {
                enterprises.push(org);
                proOrElite.push(org);
            }
        }

        return {
            proOrganizations: pros,
            eliteOrganizations: elites,
            enterpriseOrganizations: enterprises,
            proOrEliteOrganizations: proOrElite,
        };
    }, [profileOrganizations]);

    const hasAnyOrgProOrElite = proOrEliteOrganizations.length > 0;
    const hasAnyOrgPro = proOrganizations.length > 0;
    const hasAnyOrgElite = eliteOrganizations.length > 0;
    const hasAnyOrgEnterprise = enterpriseOrganizations.length > 0;

    // If you still want “only” helpers:
    const proOnly = hasAnyOrgPro && !hasAnyOrgElite && !hasAnyOrgEnterprise;
    const eliteOnly = hasAnyOrgElite && !hasAnyOrgPro && !hasAnyOrgEnterprise;
    const enterpriseOnly = hasAnyOrgEnterprise && !hasAnyOrgPro && !hasAnyOrgElite;

    return {
        hasAnyOrgProOrElite,
        proOrEliteOrganizations,
        hasAnyOrgPro,
        hasAnyOrgElite,
        hasAnyOrgEnterprise,
        proOrganizations,
        eliteOrganizations,
        enterpriseOrganizations,
        proOnly,
        eliteOnly,
        enterpriseOnly,
    };
}